/**
 * Alt text applicator — delete-and-recreate (the documented working flow).
 *
 * WHY delete+recreate and not putRecord: AT Protocol posts are content-
 * addressed and immutable. The Bluesky AppView intentionally ignores
 * putRecord on app.bsky.feed.post records — it serves the first-seen
 * version forever (atproto discussion #3038, and PRODUCT.md "Technical
 * constraints"). A putRecord alt-text edit would be stored in the PDS but
 * never shown by Bluesky clients: a silent no-op. Delete + recreate with
 * the new alt text is the only flow the AppView honors.
 *
 * Trade-off (surfaced to the user in the UI before applying): the post
 * gets a NEW URI, so likes, reposts, quotes, and replies on the original
 * are lost permanently.
 *
 * Safety: the delete carries a swapRecord guard (a concurrent edit fails
 * the delete instead of clobbering it), and if the recreate fails, the
 * ORIGINAL record content is restored as a new post so nothing is lost
 * silently.
 */

/**
 * Applies the alt-only edits for a given embed shape.
 * Mutates the passed record in place and returns it.
 *
 * @param {Object} record - Full post record value (mutated in place)
 * @param {string} embedType - app.bsky.embed.images | recordWithMedia | video
 * @param {Object} approvedAlts - Map of image index -> new alt text
 */
function applyAltEdits(record, embedType, approvedAlts) {
  const embed = record.embed;

  if (!embed) return record;

  if (embedType === 'app.bsky.embed.images') {
    for (const [idx, altText] of Object.entries(approvedAlts)) {
      const i = parseInt(idx, 10);

      if (embed.images?.[i]) embed.images[i].alt = altText;
    }
  } else if (embedType === 'app.bsky.embed.recordWithMedia') {
    const media = embed.media;

    if (media?.$type !== 'app.bsky.embed.images') return record;

    for (const [idx, altText] of Object.entries(approvedAlts)) {
      const i = parseInt(idx, 10);

      if (media.images?.[i]) media.images[i].alt = altText;
    }
  } else if (embedType === 'app.bsky.embed.video') {
    // Single alt for the whole video embed.
    const first = Object.keys(approvedAlts)[0];

    if (first !== undefined) embed.alt = approvedAlts[first];
  }

  return record;
}

/**
 * Applies approved alt text to a post via delete-and-recreate.
 *
 * 1. Fetches the current record (its cid becomes the delete swap-guard)
 * 2. Mutates ONLY the alt fields
 * 3. Deletes the original (swapRecord: cid — concurrent edits fail safely)
 * 4. Creates the new post; on failure, restores the original content as
 *    a new post so the content is never lost silently
 *
 * @param {import('@atproto/api').Agent} agent - Authenticated ATProto agent
 * @param {string} did - The user's DID
 * @param {Object} post - Post object from postScanner (rkey, embedType, originalValue)
 * @param {Object} approvedAlts - Map of image index -> new alt text
 * @returns {Promise<Object>} - Result from createRecord ({ uri, cid })
 */
export async function applyAltText(agent, did, post, approvedAlts) {
  const collection = 'app.bsky.feed.post';
  const rkey = post.rkey;

  if (!rkey) throw new Error('Post is missing a record key (rkey).');

  // 1. Fetch the current record; its cid guards the delete.
  const current = await agent.api.com.atproto.repo.getRecord({
    repo: did,
    collection,
    rkey,
  });

  // 2. Clone and mutate ONLY the alt fields.
  const record = JSON.parse(JSON.stringify(current.data.value));
  applyAltEdits(record, post.embedType || 'app.bsky.embed.images', approvedAlts);

  // 3. Delete the original with a swap guard (concurrent edit -> safe failure).
  await agent.api.com.atproto.repo.deleteRecord({
    repo: did,
    collection,
    rkey,
    swapRecord: current.data.cid,
  });

  // 4. Recreate with the alt text. On failure, restore the original content
  //    as a new post (the old URI/engagement is already gone; content isn't).
  try {
    const result = await agent.api.com.atproto.repo.createRecord({
      repo: did,
      collection,
      record,
    });

    return result.data;
  } catch (err) {
    try {
      await agent.api.com.atproto.repo.createRecord({
        repo: did,
        collection,
        record: current.data.value,
      });
      throw new Error(
        'The updated post could not be created, but your original post content was restored as a new post. ' +
          `Details: ${err.message || err}`
      );
    } catch (restoreErr) {
      throw new Error(
        'The updated post could not be created AND restoring the original failed. ' +
          `Your post content may need manual recovery. Details: ${err.message || err}; ` +
          `restore failed with: ${restoreErr?.message || restoreErr}`
      );
    }
  }
}
